import { TellescopeModelFromName } from "@tellescope/tanstack-query";
import { Enduser, Form, FormField, FormResponseValueAnswer } from "@tellescope/types-models";
import { useCallback, useMemo } from "react";
import { buildOrderedFieldList, responsesSatisfyConditions } from "../../utils";
import type { NextFieldLogicOptions } from "../../utils/types";
import { TellescopeFieldResponse } from "../../types";
import type { LocalTellescopeFormResponseMap } from "./use-tellescope-form-core";
import { resolveActiveResponses } from "./resolve-active-responses";
import type { FormShallowStoreHook } from "../../store";

export interface UseVisibleFieldsArgs {
  form: Form | undefined;
  fieldsArr: TellescopeModelFromName<"form_fields">[] | undefined;
  fieldsMap: Record<string, TellescopeModelFromName<"form_fields">> | undefined;
  currentResponses: LocalTellescopeFormResponseMap;
  urlLogicValue: string | undefined;
  enduser: TellescopeModelFromName<"endusers"> | Enduser | undefined;
  useFormShallowStore: FormShallowStoreHook;
}

export const useVisibleFields = ({
  form,
  fieldsArr,
  fieldsMap,
  currentResponses,
  urlLogicValue,
  enduser,
  useFormShallowStore,
}: UseVisibleFieldsArgs) => {
  const { includedFieldIds } = useFormShallowStore("includedFieldIds");

  const logicOptions: NextFieldLogicOptions = useMemo(
    () => ({
      urlLogicValue,
      dateOfBirth: enduser?.dateOfBirth,
      gender: enduser?.gender,
      state: enduser?.state,
      form,
      activeResponses: resolveActiveResponses({ currentResponses, includedFieldIds }),
    }),
    [currentResponses, includedFieldIds, urlLogicValue, form, enduser?.dateOfBirth, enduser?.gender, enduser?.state]
  );

  const orderedFields = useMemo(() => {
    const responsesArray = Object.values(currentResponses);
    if (!responsesArray.length) return [];
    return buildOrderedFieldList(fieldsArr ?? [], responsesArray, logicOptions);
  }, [currentResponses, fieldsArr, logicOptions]);

  const subFieldIds = useMemo(() => {
    const ids = new Set<string>();
    fieldsArr?.forEach((field) => {
      if (field.type === "Question Group") {
        field.options?.subFields?.forEach((sf) => ids.add(sf.id));
      }
    });
    return ids;
  }, [fieldsArr]);

  const isSubFieldHidden = useCallback(
    (fieldId: string) => {
      const field = fieldsMap?.[fieldId];
      if (!field) return false;
      if (!field.isInGroup && !subFieldIds.has(fieldId)) return false;
      if (!field.groupShowCondition || !Object.keys(field.groupShowCondition).length) return false;
      return !responsesSatisfyConditions(Object.values(currentResponses), field.groupShowCondition, logicOptions);
    },
    [fieldsMap, subFieldIds, currentResponses, logicOptions]
  );

  const visibleFields = useMemo(() => {
    return orderedFields.reduce<(FormField & { answer: TellescopeFieldResponse["answer"] })[]>((acc, field) => {
      if (subFieldIds.has(field.id)) return acc;

      if (
        !field.groupShowCondition ||
        !Object.keys(field.groupShowCondition).length ||
        responsesSatisfyConditions(Object.values(currentResponses), field.groupShowCondition, logicOptions)
      ) {
        acc.push({
          ...field,
          answer:
            currentResponses[field.id]?.answer ??
            ({
              type: field.type satisfies FormResponseValueAnswer["type"],
              value: undefined satisfies FormResponseValueAnswer["value"],
            } as FormResponseValueAnswer),
        });
      }
      return acc;
    }, []);
  }, [orderedFields, subFieldIds, currentResponses, logicOptions]);

  return { logicOptions, orderedFields, subFieldIds, isSubFieldHidden, visibleFields };
};
